import React from 'react';
import { AlertTriangle, Calendar, Clock, Tag } from 'lucide-react';
import {
  MERGE_TAGS, parseSchedule, schedulePresets, smsSegments, toInputs,
} from '../../lib/smsCompose.js';

/**
 * Pill bar that inserts a merge tag at the textarea caret.
 * @param {{textareaRef: React.RefObject<HTMLTextAreaElement>, value: string, onChange: (v: string) => void}} props
 */
export function MergeTagBar({ textareaRef, value, onChange }) {
  function insert(tag) {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${tag}${value.slice(end)}`);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange?.(start + tag.length, start + tag.length);
    });
  }
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1" aria-label="Merge tags">
      <Tag className="w-3.5 h-3.5 text-white/40 shrink-0" />
      {MERGE_TAGS.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => insert(t)}
          className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-civix-purple/15 text-civix-purpleSoft border border-civix-purple/30 hover:bg-civix-purple/30 transition cursor-pointer shrink-0"
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/** @param {{text: string}} props Character + segment counter. */
export function SmsCounter({ text }) {
  const { chars, segments, unicode, single } = smsSegments(text);
  const over = chars > single;
  return (
    <div className={`text-[10px] flex items-center gap-1 ${over ? 'text-amber-300' : 'text-white/40'}`}>
      {over && <AlertTriangle className="w-3 h-3" />}
      {chars}/{single} · {segments} SMS segment{segments === 1 ? '' : 's'}{unicode ? ' (special characters)' : ''}
    </div>
  );
}

/**
 * "Schedule for later" toggle with date/time inputs and quick presets.
 * @param {Object} props
 * @param {{on: boolean, date: string, time: string}} props.value
 * @param {(v: {on: boolean, date: string, time: string}) => void} props.onChange
 */
export function SchedulePicker({ value, onChange }) {
  const today = toInputs(new Date()).date;
  const { error } = value.on ? parseSchedule(value.date, value.time) : { error: '' };
  const field = 'bg-civix-panelLight border border-civix-border text-white text-xs rounded-xl px-3 py-2 focus:outline-hidden focus:border-civix-purple';
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer">
        <input
          type="checkbox"
          className="accent-civix-purple"
          checked={value.on}
          onChange={(e) => onChange({ ...value, on: e.target.checked, ...(e.target.checked && !value.date ? toInputs(new Date(Date.now() + 3600_000)) : {}) })}
        />
        <Calendar className="w-3.5 h-3.5 text-civix-purpleSoft" /> Schedule for later
      </label>
      {value.on && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {schedulePresets().map((p) => (
              <button key={p.label} type="button" onClick={() => onChange({ on: true, ...toInputs(p.at) })}
                className="text-[11px] px-2 py-1 rounded-md border border-civix-border text-white/70 hover:border-civix-cyan/60 hover:text-civix-cyan transition">
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] uppercase tracking-wider text-white/40">
              Date
              <input type="date" min={today} value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} className={`${field} w-full mt-1`} />
            </label>
            <label className="text-[10px] uppercase tracking-wider text-white/40">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Time</span>
              <input type="time" value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} className={`${field} w-full mt-1`} />
            </label>
          </div>
          {error && <p className="text-[11px] text-red-300">{error}</p>}
        </>
      )}
    </div>
  );
}
